import type { SoundBinding, SoundLibraryData } from '@tanky/shared';
import { api } from '../api';
import { socket } from '../ws';

/**
 * Knihovna zvuků ze serveru: seznam souborů, přiřazení k akcím a dekódované
 * AudioBuffery (stahují se líně a pamatují si je).
 */
export class SoundLibrary {
  data: SoundLibraryData | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private pending = new Map<string, Promise<AudioBuffer | null>>();
  private loadedAt = new Map<string, number>();
  private byAction = new Map<string, SoundBinding[]>();
  private listeners = new Set<() => void>();
  private loading: Promise<void> | null = null;
  private ctx: () => BaseAudioContext | null = () => null;

  constructor() {
    socket.on((m) => {
      if (m.t === 'sounds_changed' && m.version !== this.data?.version) this.reload();
    });
  }

  /** Kontext pro dekódování (dodá engine, až vznikne). */
  setContext(get: () => BaseAudioContext | null) {
    this.ctx = get;
  }

  onChange(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** Zneplatní zachešovaný zvuk, aby se při dalším použití stáhl znovu. */
  invalidate(id?: string) {
    if (id) {
      this.buffers.delete(id);
      this.pending.delete(id);
      this.loadedAt.delete(id);
    } else {
      this.buffers.clear();
      this.pending.clear();
      this.loadedAt.clear();
    }
  }

  load(): Promise<void> {
    this.loading ??= this.reload();
    return this.loading;
  }

  async reload(): Promise<void> {
    try {
      const data = await api.sounds();
      this.data = data;
      this.byAction.clear();
      for (const b of data.bindings) {
        const list = this.byAction.get(b.action) ?? [];
        list.push(b);
        this.byAction.set(b.action, list);
      }
      // Zahodit buffery smazaných nebo zastaralých zvuků
      const soundsById = new Map(data.sounds.map((s) => [s.id, s]));
      for (const [id] of [...this.buffers.entries()]) {
        const info = soundsById.get(id);
        if (!info || this.loadedAt.get(id) !== info.createdAt) {
          this.buffers.delete(id);
          this.pending.delete(id);
          this.loadedAt.delete(id);
        }
      }
      this.preload();
      for (const l of this.listeners) l();
    } catch {
      /* nepřihlášený / bez spojení – zkusí se znovu při dalším načtení */
      this.loading = null;
    }
  }

  bindings(action: string): SoundBinding[] {
    return this.byAction.get(action) ?? [];
  }

  /** Dekódovaný zvuk, nebo null (a spustí stahování, pokud ještě neběží). */
  buffer(id: string): AudioBuffer | null {
    const b = this.buffers.get(id);
    if (b) return b;
    void this.fetch(id);
    return null;
  }

  fetch(id: string): Promise<AudioBuffer | null> {
    const have = this.buffers.get(id);
    if (have) return Promise.resolve(have);
    let p = this.pending.get(id);
    if (p) return p;
    const ctx = this.ctx();
    if (!ctx) return Promise.resolve(null);
    const sound = this.data?.sounds.find((s) => s.id === id);
    p = fetch(api.soundUrl(id, sound?.createdAt), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
      .then((data) => ctx.decodeAudioData(data))
      .then((buf) => {
        this.buffers.set(id, buf);
        this.loadedAt.set(id, sound?.createdAt ?? 0);
        return buf;
      })
      .catch(() => null)
      .finally(() => this.pending.delete(id));
    this.pending.set(id, p);
    return p;
  }

  /** Předem stáhnout všechny přiřazené zvuky, ať první přehrání nezmešká. */
  preload() {
    if (!this.data || !this.ctx()) return;
    const used = new Set(this.data.bindings.map((b) => b.soundId));
    for (const id of used) void this.fetch(id);
  }
}
